<script setup lang="ts">
import { computed } from 'vue'
import { Search } from 'lucide-vue-next'
import { Input } from '@/components/shadcn'
import { JButton } from '@/components/atoms'

type StyleType =
  | 'default'   // 기본 크기
  | 'sm'        // 작은 크기
  | 'lg'        // 큰 크기
  | 'error'     // 에러 상태
  | 'success'   // 성공 상태
  | 'warning'   // 경고 상태

const props = withDefaults(
  defineProps<{
    modelValue?: string | number
    placeholder?: string
    /** 입력란 비활성화 (버튼도 함께 비활성화) */
    disabled?: boolean
    /** 입력란을 읽기 전용으로 (버튼은 동작). 팝업으로만 값을 채울 때 true */
    readonly?: boolean
    /** 찾기 버튼 텍스트 */
    buttonText?: string
    /** 찾기 버튼만 별도 비활성화 */
    buttonDisabled?: boolean
    /** 찾기 버튼에 돋보기 아이콘 표시 여부 */
    showIcon?: boolean
    name?: string
    id?: string
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
  }>(),
  {
    placeholder: '',
    disabled: false,
    readonly: true,
    buttonText: '찾기',
    buttonDisabled: false,
    showIcon: true,
    styletype: 'default',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  'change': [value: string | number]
  /** 찾기 버튼 클릭 */
  'search': []
  'focus': [event: FocusEvent]
  'blur': [event: FocusEvent]
}>()

const INPUT_STYLE: Record<StyleType, string> = {
  default: '',
  sm: 'h-7 text-xs px-2',
  lg: 'h-12 text-base px-4',
  error: 'border-destructive focus-visible:ring-destructive',
  success: 'border-green-500 focus-visible:ring-green-500',
  warning: 'border-amber-500 focus-visible:ring-amber-500',
}

// JButton styletype 매핑 (크기만 맞춤)
const BUTTON_STYLETYPE_MAP: Record<StyleType, 'sm' | 'default' | 'lg'> = {
  default: 'default',
  sm: 'sm',
  lg: 'lg',
  error: 'default',
  success: 'default',
  warning: 'default',
}

const inputClass = computed(() =>
  [INPUT_STYLE[props.styletype], props.class].filter(Boolean).join(' '),
)

const buttonStyletype = computed(() => BUTTON_STYLETYPE_MAP[props.styletype])

const isButtonDisabled = computed(() => props.disabled || props.buttonDisabled)

const handleInput = (value: string | number) => {
  emit('update:modelValue', value)
  emit('change', value)
}

const handleSearch = () => {
  emit('search')
}
</script>

<template>
  <div class="flex items-center gap-1">
    <Input
      :id="id"
      :model-value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :name="name"
      :class="inputClass"
      class="flex-1 min-w-0"
      @update:model-value="handleInput"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
    />
    <JButton
      type="button"
      variant="outline"
      :styletype="buttonStyletype"
      :disabled="isButtonDisabled"
      class="shrink-0"
      @click="handleSearch"
    >
      <Search v-if="showIcon" class="h-3.5 w-3.5" />
      {{ buttonText }}
    </JButton>
  </div>
</template>
